// Shared address input composite: Country + Street (Google Places) + City +
// State/Province + ZIP/Postal (BL-935 — merges admin's AddressFormFields.tsx,
// which becomes the base for its richer country/lockedCountry/state-dropdown
// feature set, with bidder's postalCode field naming and brandColor dialect
// need). Every address form in either app should use this component.
'use client';

import { useCallback, useContext } from 'react';
import { clsx } from 'clsx';
import { AddressAutocomplete, type AddressResult } from './AddressAutocomplete';
import { Select } from './Select';
import { FieldLabel as SharedFieldLabel, FieldLayoutContext, FieldRow, FieldSizeContext, STACKED_LABEL_CLASS_TOUCH } from './FormField';
import {
    COUNTRIES,
    getCountryIsoCode,
    getStateLabelForCountry,
    getStateOptionsForCountry,
    getZipLabelForCountry,
    type StateOption,
} from './address-constants';

/** Structured address value stored by a form. */
export interface AddressValue {
    street: string;
    city: string;
    /** 2-char state/province code when country has a dropdown, free text otherwise. */
    state: string;
    postalCode: string;
    /** Full country name ("United States", "Canada", "Mexico", or free-text for other). */
    country: string;
}

/** Per-field validation errors (optional). */
export type AddressErrors = Partial<Record<keyof AddressValue, string>>;

/** Props for the AddressFormFields component. */
export interface AddressFormFieldsProps {
    value: AddressValue;
    onChange: (next: AddressValue) => void;
    errors?: AddressErrors;
    disabled?: boolean;
    /** Hide the country selector when the form only supports a single country. Defaults to true. */
    showCountry?: boolean;
    /**
     * When set, the country is locked to this value — the country selector is hidden,
     * Google Places suggestions are restricted to this country, and any autocomplete
     * result from a different country is coerced back to the locked country.
     * Use this in forms whose parent state does not carry a country field.
     */
    lockedCountry?: string;
    /**
     * Restricts street-autocomplete suggestions to these 2-letter ISO country
     * codes when the form is NOT locked to a single country (`lockedCountry`
     * always wins when set). Omitted by default — worldwide suggestions.
     * Bidder's forms pass `['us', 'ca', 'mx']` here to keep their pre-existing
     * restriction; admin's unlocked forms (e.g. tenant creation) omit it.
     */
    restrictToCountries?: string[];
    /** Custom labels for individual fields. */
    labels?: Partial<Record<keyof AddressValue, string>>;
    /**
     * When true, appends a red asterisk to the address's genuinely-required
     * fields — **Street, City, State only** — matching the shared `AddressSchema`
     * (street/city/state `.min(1)`; postalCode/country `.optional()`). ZIP and
     * Country are optional everywhere and NEVER get an asterisk, so a form can
     * pass `required` without falsely marking them. Keeping this single boolean
     * (rather than a per-field map) enforces one uniform requiredness contract
     * across every address form in both apps.
     */
    required?: boolean;
    className?: string;
    /**
     * Pre-validated brand color for the street-autocomplete focus ring (a
     * per-app dialect prop — bidder's tenant-branded forms pass this; admin's
     * forms omit it and get the static `ring-primary` token instead).
     */
    brandColor?: string;
    /**
     * Automation-handle namespace (BL-2136): `<prefix>-street`, `-city`,
     * `-state`, `-postalCode` on the controls. Nothing without one.
     */
    testIdPrefix?: string;
    /**
     * City, State and ZIP each on a full-width row of their own instead of
     * wrapping side by side (BUG-2040): for a narrow card whose every field is
     * one row, such as the invoice Bill to editor. Labels stay stacked on top,
     * and the rows take admin's cluster tier, the card's own field rhythm.
     */
    stacked?: boolean;
}

/** Returns an empty AddressValue with United States as the default country. */
export function emptyAddress(): AddressValue {
    return { street: '', city: '', state: '', postalCode: '', country: 'United States' };
}

const DEFAULT_LABELS = {
    street: 'Street address',
    city: 'City',
    country: 'Country',
} as const;

/**
 * The address fields that are genuinely required (mirror of `AddressSchema`'s
 * `.min(1)` set). The `required` prop's asterisk applies ONLY to these — ZIP and
 * Country are optional everywhere, so they never get one.
 */
const REQUIRED_ADDRESS_FIELDS = new Set<keyof AddressValue>(['street', 'city', 'state']);

const inputClasses = (hasError: boolean, disabled: boolean, touch: boolean) =>
    clsx(
        'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent text-primary',
        // BL-2754: on a phone sheet the sheet field size, as TextInput's touch dialect.
        touch ? 'min-h-12 text-body-lg' : 'text-sm',
        hasError
            ? 'border-red-400 focus:ring-red-400'
            : 'border-zinc-300 focus:ring-secondary',
        disabled && 'opacity-50 cursor-not-allowed bg-zinc-50',
    );

/** The touch flag every sub-field reads: a phone sheet sets FieldSizeContext to `touch`. */
function useTouch(): boolean {
    return useContext(FieldSizeContext) === 'touch';
}

/** The error line under a sub-field: 14px on a phone sheet (FormField's touch error), 12px elsewhere. */
function FieldError({ message }: { message?: string }) {
    const touch = useTouch();
    if (!message) return null;
    return <p className={touch ? 'mt-1 text-body text-red-600' : 'mt-1 text-xs text-red-600'}>{message}</p>;
}

/**
 * Single labelled wrapper used by all sub-fields.
 *
 * BL-1087: the label + required marker now come from the shared `FieldLabel`
 * in `FormField.tsx` rather than a fourth private copy of the markup, so the
 * marker can't drift from the other admin forms again.
 */
function FieldLabel({ label, required, markClassName, children }: { label: string; required?: boolean; markClassName?: string; children: React.ReactNode }) {
    // BUG-1991: inside a label-left form (the lot drawer's row layout) each
    // sub-field is a row like every other field, not a label stacked on top.
    const layout = useContext(FieldLayoutContext);
    const touch = useTouch();
    if (layout === 'row') {
        return (
            <FieldRow label={label} required={required}>
                {children}
            </FieldRow>
        );
    }
    return (
        <div>
            <SharedFieldLabel
                label={label}
                required={required}
                className={touch ? STACKED_LABEL_CLASS_TOUCH : 'block text-xs font-medium text-zinc-600 mb-1'}
                markClassName={markClassName}
            />
            {children}
        </div>
    );
}

/** Country selector — every country is directly selectable, no free-text escape. */
function CountryField({
    value,
    onCountryChange,
    label,
    error,
    disabled,
    required,
}: {
    value: string;
    onCountryChange: (next: string) => void;
    label: string;
    error?: string;
    disabled: boolean;
    required?: boolean;
}) {
    return (
        <FieldLabel label={label} required={required}>
            <Select
                value={value}
                onChange={onCountryChange}
                options={COUNTRIES}
                placeholder="Select…"
                searchable
                disabled={disabled}
                aria-label={label}
            />
            <FieldError message={error} />
        </FieldLabel>
    );
}

/**
 * State/Province field — Select for US/CA, free text for Other.
 * Preserves legacy values that don't match the current country's option list
 * by injecting a synthetic "(unrecognized)" option so data is visible and editable.
 */
function StateField({
    value,
    onChange,
    options,
    label,
    error,
    disabled,
    required,
    testId,
}: {
    value: string;
    onChange: (next: string) => void;
    options: StateOption[] | null;
    label: string;
    error?: string;
    disabled: boolean;
    required?: boolean;
    testId?: string;
}) {
    const effectiveOptions = (() => {
        if (!options) return null;
        if (!value) return options;
        const matches = options.some((o) => o.value === value);
        if (matches) return options;
        // Legacy / unrecognized value — surface it at the top so the user can see
        // their current state and re-select a canonical code.
        return [{ value, label: `${value} (unrecognized)` }, ...options];
    })();

    const touch = useTouch();
    return (
        <FieldLabel label={label} required={required}>
            {effectiveOptions ? (
                <Select
                    value={value}
                    onChange={onChange}
                    options={effectiveOptions}
                    placeholder="Select…"
                    searchable
                    disabled={disabled}
                    testId={testId}
                    // The sibling <label> has no htmlFor, and a combobox falls back to its chosen
                    // value for a name ("Iowa") — name it by the field instead (BUG-2549).
                    aria-label={label}
                />
            ) : (
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={disabled}
                    data-testid={testId}
                    // FieldLabel renders the <label> as a sibling with no htmlFor (the
                    // sub-controls are a mix of inputs, Select triggers and the
                    // AddressAutocomplete box, none of which accept an id). aria-label
                    // gives each control its own accessible name so screen readers — and
                    // getByLabelText in tests — can resolve it.
                    aria-label={label}
                    className={inputClasses(Boolean(error), disabled, touch)}
                />
            )}
            <FieldError message={error} />
        </FieldLabel>
    );
}

/** Plain text field used for City and ZIP. */
function PlainTextField({
    value,
    onChange,
    label,
    error,
    disabled,
    required,
    testId,
}: {
    value: string;
    onChange: (next: string) => void;
    label: string;
    error?: string;
    disabled: boolean;
    required?: boolean;
    testId?: string;
}) {
    const touch = useTouch();
    return (
        <FieldLabel label={label} required={required}>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                data-testid={testId}
                // See StateField — the sibling <label> carries no htmlFor, so each
                // control names itself.
                aria-label={label}
                className={inputClasses(Boolean(error), disabled, touch)}
            />
            <FieldError message={error} />
        </FieldLabel>
    );
}

/**
 * Address input composite with Google Places autocomplete, country selector,
 * and country-aware state/ZIP dropdowns for US, Canada, and Mexico.
 */
export function AddressFormFields({
    value,
    onChange,
    errors,
    disabled = false,
    showCountry = true,
    lockedCountry,
    restrictToCountries: callerRestrictToCountries,
    labels,
    required = false,
    className,
    brandColor,
    testIdPrefix,
    stacked = false,
}: AddressFormFieldsProps) {
    const handle = (field: string) => (testIdPrefix ? `${testIdPrefix}-${field}` : undefined);
    // When lockedCountry is set, the country selector is hidden and the effective
    // country for state/zip labels and autocomplete restrictions comes from lockedCountry.
    const effectiveCountry = lockedCountry ?? value.country;
    const stateOptions = getStateOptionsForCountry(effectiveCountry);
    const streetLabel = labels?.street ?? DEFAULT_LABELS.street;
    const stateLabel = labels?.state ?? getStateLabelForCountry(effectiveCountry);
    const zipLabel = labels?.postalCode ?? getZipLabelForCountry(effectiveCountry);

    const isoCode = getCountryIsoCode(effectiveCountry);
    // A locked country always wins (belt-and-suspenders with handleAddressSelect's
    // own coercion below); otherwise fall back to the caller's own default
    // (bidder's forms pass ['us','ca','mx']; admin's unlocked forms omit it).
    const restrictToCountries = lockedCountry && isoCode ? [isoCode] : callerRestrictToCountries;

    // The asterisk only ever marks street/city/state (see REQUIRED_ADDRESS_FIELDS);
    // ZIP/Country stay unmarked even when the form as a whole is `required`.
    const isFieldRequired = (field: keyof AddressValue) => required && REQUIRED_ADDRESS_FIELDS.has(field);

    const set = (patch: Partial<AddressValue>) => onChange({ ...value, ...patch });

    /**
     * Apply a Google Places result atomically to all fields.
     *
     * Two behaviors:
     *   1. `lockedCountry` set → autocomplete is already restricted to that country
     *      via `restrictToCountries`, but as a belt-and-suspenders safeguard we also
     *      coerce the result here (state is cleared if the result somehow came back
     *      from a different country).
     *   2. `lockedCountry` NOT set → the result's country is propagated up, which
     *      also flips the state label (State/Province) and options (US/CA) for the
     *      next render. Parents that hide the country selector (`showCountry={false}`)
     *      but don't set `lockedCountry` will see this country switch reflected in
     *      labels — if that's surprising, use `lockedCountry` instead.
     */
    const handleAddressSelect = useCallback(
        (result: AddressResult) => {
            if (lockedCountry) {
                onChange({
                    street: result.street,
                    city: result.city,
                    state: result.country === lockedCountry ? result.state : '',
                    postalCode: result.postalCode,
                    country: lockedCountry,
                });
                return;
            }
            const nextCountry = result.country || value.country;
            onChange({
                street: result.street,
                city: result.city,
                state: result.state,
                postalCode: result.postalCode,
                country: nextCountry,
            });
        },
        [onChange, value.country, lockedCountry],
    );

    /** When the user switches country, clear the state field — codes are not interchangeable. */
    const handleCountryChange = (nextCountry: string) => {
        onChange({ ...value, country: nextCountry, state: '' });
    };

    const showCountrySelector = showCountry && !lockedCountry;
    // BUG-1991: in a label-left form every sub-field is its own row, at the
    // form's row rhythm, with City / State / ZIP stacked rather than side by side.
    const isRowLayout = useContext(FieldLayoutContext) === 'row';
    // A phone sheet's touch rows carry their own padding (BL-2111), so the
    // 4px rhythm applies to desktop rows only — as TitleMailingFields does.
    const touch = useContext(FieldSizeContext) === 'touch';
    const rowGapClass = touch ? undefined : 'space-y-density-tight';
    // BL-2754: on a phone sheet (stacked layout) every sub-field is its own row, 24px apart:
    // `.ui-modal--sheet` sets `--density-group-gap` to 24px (BL-2402), so the group tier is 24px there.
    const stackClass = touch ? 'space-y-density-group' : stacked ? 'space-y-density-cluster' : undefined;
    // Side by side only when neither the row layout, `stacked` nor a phone sheet puts each sub-field on its own row.
    const wraps = !isRowLayout && !stackClass;

    return (
        <div className={clsx(isRowLayout ? rowGapClass : stackClass ?? 'space-y-4', className)}>
            {showCountrySelector && (
                <CountryField
                    value={value.country}
                    onCountryChange={handleCountryChange}
                    label={labels?.country ?? DEFAULT_LABELS.country}
                    error={errors?.country}
                    disabled={disabled}
                    required={isFieldRequired('country')}
                />
            )}

            <FieldLabel label={streetLabel} required={isFieldRequired('street')}>
                <AddressAutocomplete
                    value={value.street}
                    onChange={(v) => set({ street: v })}
                    onAddressSelect={handleAddressSelect}
                    error={errors?.street}
                    disabled={disabled}
                    placeholder="Start typing to search…"
                    ariaLabel={streetLabel}
                    restrictToCountries={restrictToCountries}
                    brandColor={brandColor}
                    testId={handle('street')}
                />
            </FieldLabel>

            {/* City / state / zip — wraps to two rows on narrow containers so the state
          dropdown always has enough room to render. */}
            <div className={isRowLayout ? rowGapClass : stackClass ?? 'flex flex-wrap gap-3'}>
                <div className={clsx(wraps && 'flex-1 min-w-[160px]')}>
                    <PlainTextField
                        value={value.city}
                        onChange={(v) => set({ city: v })}
                        label={labels?.city ?? DEFAULT_LABELS.city}
                        error={errors?.city}
                        disabled={disabled}
                        required={isFieldRequired('city')}
                        testId={handle('city')}
                    />
                </div>
                <div className={clsx(wraps && 'flex-1 min-w-[140px]')}>
                    <StateField
                        value={value.state}
                        onChange={(v) => set({ state: v })}
                        options={stateOptions}
                        label={stateLabel}
                        error={errors?.state}
                        disabled={disabled}
                        required={isFieldRequired('state')}
                        testId={handle('state')}
                    />
                </div>
                <div className={clsx(wraps && 'flex-1 min-w-[110px]')}>
                    <PlainTextField
                        value={value.postalCode}
                        onChange={(v) => set({ postalCode: v })}
                        label={zipLabel}
                        error={errors?.postalCode}
                        disabled={disabled}
                        required={isFieldRequired('postalCode')}
                        testId={handle('postalCode')}
                    />
                </div>
            </div>
        </div>
    );
}
